import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import sidebarStyles from '~/styles/bookingFlow/sidebar.module.css';
import cnh from '~/helpers/cnh';
import IconChevronDown from '~icons/tabler/chevron-down';

type Props = {
  children: React.ReactNode;
  className?: string;
};

export default function BookingFlowSidebar({
  children,
  className,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const sidebarId = useId();

  useEffect(() => {
    if (!toggleRef.current) {
      return;
    }

    // Because of using container queries, we cannot check using
    // matchMedia. We need to check the computed style of the button, which
    // is hidden or visible based on the container query.
    const toggleDisplayState = window.getComputedStyle(toggleRef.current).display;

    if (toggleDisplayState !== 'flex') {
      setIsOpen(true);
    }
  }, [toggleRef]);

  const onToggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  return (
    <div className={cnh(sidebarStyles.sidebar, className)}>
      <button
        type="button"
        className={sidebarStyles.sidebarToggleButton}
        onClick={onToggleOpen}
        ref={toggleRef}
        aria-controls={sidebarId}
        aria-expanded={isOpen}
      >
        <IconChevronDown />
        {isOpen ? 'Hide summary' : 'View summary'}
      </button>

      <div
        className={cnh(sidebarStyles.sidebarContent, isOpen ? sidebarStyles.sidebarContentOpen : '')}
        id={sidebarId}
      >
        {children}
      </div>
    </div>
  );
}
